import { useEffect, useId, useMemo, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { Check, RefreshCw, Search } from 'lucide-react'
import {
  fetchLieuDeDepart,
  fetchLieux,
  searchNumbers,
  selectOnboardingNumber,
  type AvailableNumber,
  type NumberType,
} from '@/api/onboarding'
import { ApiHttpError } from '@/api/auth'
import { validatePhoneNumber } from '@/lib/phoneValidation'
import { SelecteurPays, type PaysNumero } from '@/components/SelecteurPays'
import { cn } from '@/lib/utils'
import { defaultLocale } from '@/lib/locales'
import { lire as lireNumeroSouhaite, oublier as oublierNumeroSouhaite } from '@/lib/numeroSouhaite'
import { garderLieu, lieuDeDepart, lieuGarde, libelleLieu, suggerer, type Suggestion } from '@/lib/lieuxNumeros'
import { etape } from '@/lib/analytics/inscription'
import { CadreAvant, CHAMP_SOMBRE, Erreur, Etiquette } from '@/pages/onboarding/AvantPaiement'

type Mode = 'local' | 'tollfree' | 'port'

/** Numbers fetched per search, shown six at a time: « Voir d'autres numéros » pages through them first. */
const LOT = 18
const PAGE = 6

/**
 * One list per kind of number. Switching tabs never resets it: the search, the
 * page and the chosen number are still there when the visitor comes back. The
 * transfer tab uses the local list (its temporary number is a local number).
 */
interface EtatListe {
  /** City id (qc-dorval) or area code (514); empty for a random search. */
  lieu: string
  /** Bumped by « Voir d'autres numéros » once the lot is exhausted: a fresh lot from the server. */
  tirage: number
  page: number
  choisi: string
}

const MUET = { color: '#8B9BB4' }

function formatPhoneNumber(number: string) {
  const cleaned = number.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return number
}

function listeVide(lieu = ''): EtatListe {
  return { lieu, tirage: 0, page: 0, choisi: '' }
}

export function OnboardingStep1Page() {
  // Repere : cet ecran de l'onboarding a ete atteint.
  useEffect(() => {
    etape('onboarding_etape1')
  }, [])

  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Paid, but the chosen number could not be bought: the server cleared the
  // choice and sent the visitor back here, with the area code of the lost number.
  const [params] = useSearchParams()
  const indisponible = params.get('indisponible') === '1'
  const lieuPerdu = /^\d{3}$/.test(params.get('lieu') || '') ? params.get('lieu')! : undefined

  // Ce que le visiteur avait choisi sur une page publique avant d'avoir un
  // compte. Lu une seule fois, à la construction : ce n'est pas un état qui
  // évolue, c'est une valeur de départ.
  const [souhait] = useState(() => lireNumeroSouhaite())

  const [mode, setMode] = useState<Mode>('local')
  const [pays, setPays] = useState<PaysNumero>('CA')
  // An area code brought from a public page beats any guess.
  const [lieuImpose] = useState(() => souhait?.area || (indisponible ? lieuGarde() || lieuPerdu : undefined))
  const [local, setLocal] = useState<EtatListe>(() => listeVide(lieuImpose))
  const [sansFrais, setSansFrais] = useState<EtatListe>(() => listeVide())
  const [portNumber, setPortNumber] = useState('')
  const [portError, setPortError] = useState('')
  /** What the visitor is typing in the place field; null when it shows the chosen place. */
  const [saisie, setSaisie] = useState<string | null>(null)

  const enSansFrais = mode === 'tollfree'
  const liste = enSansFrais ? sansFrais : local
  const setListe = enSansFrais ? setSansFrais : setLocal

  const catalogue = useQuery({
    queryKey: ['lieux-numeros'],
    queryFn: fetchLieux,
    staleTime: Infinity,
    gcTime: Infinity,
  })

  // Otherwise the first place is the city of the connection, read by the server
  // from the IP address; the time zone and the language only as a fallback. On
  // Oct 28, 2026, a Mac on Toronto time with an English page was offered Toronto
  // from Québec City.
  const depart = useQuery({
    queryKey: ['lieu-de-depart'],
    queryFn: fetchLieuDeDepart,
    enabled: !lieuImpose,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })
  const attenteLieu = pays === 'CA' && !local.lieu
  useEffect(() => {
    if (!attenteLieu || depart.isLoading) return
    setLocal((e) => (e.lieu ? e : listeVide(depart.data ?? lieuDeDepart(locale))))
  }, [attenteLieu, depart.isLoading, depart.data, locale])

  // A list never changes under the visitor's eyes: no refetch on focus or on
  // return, only when they choose a place or ask for other numbers.
  const options = {
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (n: number, err: unknown) => !(isAxiosError(err) && (err.response?.status ?? 500) < 500) && n < 1,
  } as const
  const chercher = (type: NumberType, e: EtatListe) =>
    searchNumbers({
      country: pays,
      type,
      lieu: e.lieu,
      limit: LOT,
      nouveau: e.tirage > 0,
    })
  const requeteLocale = useQuery({
    queryKey: ['numeros-inscription', pays, 'local', local.lieu, local.tirage],
    queryFn: () => chercher('local', local),
    enabled: !enSansFrais && !attenteLieu,
    ...options,
  })
  const requeteSansFrais = useQuery({
    queryKey: ['numeros-inscription', pays, 'tollfree', sansFrais.tirage],
    queryFn: () => chercher('tollfree', sansFrais),
    enabled: enSansFrais,
    ...options,
  })
  const requete = enSansFrais ? requeteSansFrais : requeteLocale

  // The number clicked on a public page goes first, if it is still free.
  const lot = useMemo<AvailableNumber[]>(() => {
    const numeros = requete.data?.numbers ?? []
    const voulu = !enSansFrais ? souhait?.number : undefined
    const i = voulu ? numeros.findIndex((n) => n.phoneNumber === voulu) : -1
    return i > 0 ? [numeros[i], ...numeros.slice(0, i), ...numeros.slice(i + 1)] : numeros
  }, [requete.data, enSansFrais, souhait])
  const visibles = useMemo(() => lot.slice(liste.page * PAGE, liste.page * PAGE + PAGE), [lot, liste.page])

  // A number is always chosen, and always one of those on screen: the first one
  // until the visitor clicks another.
  useEffect(() => {
    if (visibles.length && !visibles.some((n) => n.phoneNumber === liste.choisi)) {
      setListe((e) => ({ ...e, choisi: visibles[0].phoneNumber }))
    }
  }, [visibles, liste.choisi, setListe])

  // Only the number shown as chosen is sent. On Oct 24, 2026 (company 280), a
  // tab switch reset everything and « Continuer » saved a number the person
  // had never seen selected.
  const numeroEnvoye = visibles.some((n) => n.phoneNumber === liste.choisi) ? liste.choisi : ''

  const autresNumeros = () => {
    if ((liste.page + 1) * PAGE < lot.length) setListe((e) => ({ ...e, page: e.page + 1 }))
    else setListe((e) => ({ ...e, tirage: e.tirage + 1, page: 0 }))
  }

  // Changer de pays relance la recherche : ni le lieu ni le numéro d'avant ne valent plus.
  const changerPays = (p: PaysNumero) => {
    if (p === pays) return
    setPays(p)
    // Back in Canada, the first place is guessed again (see attenteLieu).
    setLocal(listeVide())
    setSansFrais(listeVide())
    setSaisie(null)
  }

  // --- City or area code -----------------------------------------------------
  const idChamp = useId()
  const [actif, setActif] = useState(0)
  const resultat = requeteLocale.data
  const libelleCourant =
    libelleLieu(catalogue.data, local.lieu) ||
    (resultat?.lieu?.nom ? `${resultat.lieu.nom}, ${resultat.lieu.province}` : '') ||
    local.lieu
  const suggestions = useMemo(
    () => (saisie === null ? [] : suggerer(catalogue.data, saisie, pays)),
    [catalogue.data, saisie, pays],
  )
  const ouvert = saisie !== null && saisie.trim() !== ''

  const choisirLieu = (s: Suggestion) => {
    setLocal((e) => (e.lieu === s.lieu ? e : listeVide(s.lieu)))
    setSaisie(null)
  }

  const surTouche = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!suggestions.length) return
      const pas = e.key === 'ArrowDown' ? 1 : -1
      setActif((a) => (a + pas + suggestions.length) % suggestions.length)
    } else if (e.key === 'Enter') {
      // Enter picks a suggestion; from this field it never submits the form.
      e.preventDefault()
      if (ouvert && suggestions[actif]) choisirLieu(suggestions[actif])
    } else if (e.key === 'Escape') {
      setSaisie(null)
    }
  }

  // --- Submit ---------------------------------------------------------------------
  const selectMutation = useMutation({
    mutationFn: async (numero: string) => {
      if (!numero) throw new Error('NO_SELECTION')
      if (mode === 'port') {
        const v = validatePhoneNumber(portNumber)
        if (!v.isValid) throw new Error(v.error || 'INVALID_FORMAT')
      }
      await selectOnboardingNumber({
        phone_number: numero,
        wants_port: mode === 'port',
        port_number: mode === 'port' ? portNumber : '',
        is_toll_free: mode === 'tollfree',
      })
    },
    onSuccess: () => {
      oublierNumeroSouhaite()
      // Kept in case this number cannot be bought later: the search resumes here.
      if (mode !== 'tollfree') garderLieu(local.lieu)
      if (indisponible) {
        // Already paid: the welcome screen buys the new choice right away.
        void queryClient.invalidateQueries({ queryKey: ['companies'] })
        navigate(`/${locale}/onboarding/welcome`, { replace: true, state: { fromCheckout: true } })
        return
      }
      navigate(`/${locale}/onboarding/step2`, { replace: true })
    },
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        if (error.status === 401) return // l'interceptor gère la redirect
        const data = error.data as { error?: string; detail?: string }
        toast.error(data?.error || data?.detail || 'Erreur lors de la sauvegarde')
        return
      }
      if (error instanceof Error) {
        if (error.message === 'NO_SELECTION') {
          toast.error(t('onboarding.step1.errors.selectNumber'))
          return
        }
        toast.error(error.message)
        return
      }
      console.error('[OnboardingStep1]', error)
      toast.error(t('toasts.common.saveError'))
    },
  })

  const handlePortInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPortNumber(value)
    if (!value.trim()) {
      setPortError('')
      return
    }
    const v = validatePhoneNumber(value)
    setPortError(v.isValid ? '' : v.error || '')
  }

  // --- Shown above the list -------------------------------------------------------
  const res = requete.data
  let avis = ''
  if (!enSansFrais && res?.numbers.length) {
    if (res.proches) avis = t('onboarding.step1.v3.proches', { ville: res.proches })
    else if (res.repli)
      avis = t('onboarding.step1.v3.repli', {
        demande: res.repli.demande,
        servis: res.repli.servis.join(', '),
      })
  }
  const tropDeRecherches = isAxiosError(requete.error) && requete.error.response?.status === 429

  const continuer =
    mode === 'port'
      ? t('onboarding.miseEnService.continue')
      : numeroEnvoye
        ? t('onboarding.step1.v3.continuerAvec', {
            numero: formatPhoneNumber(numeroEnvoye),
          })
        : t('onboarding.step1.continueWith')

  const champLieu = (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/45" />
      <input
        id={idChamp}
        role="combobox"
        aria-label={t('onboarding.step1.v3.rechercheAide')}
        aria-expanded={ouvert}
        aria-controls={`${idChamp}-liste`}
        aria-activedescendant={ouvert && suggestions[actif] ? `${idChamp}-${actif}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        // A city first; an area code also works, only said when no city matches.
        placeholder={t('onboarding.step1.v3.recherche')}
        value={saisie ?? libelleCourant}
        onFocus={(e) => {
          setSaisie(libelleCourant)
          e.target.select()
        }}
        onChange={(e) => {
          setSaisie(e.target.value)
          setActif(0)
        }}
        onBlur={() => setSaisie(null)}
        onKeyDown={surTouche}
        className={cn(CHAMP_SOMBRE, 'pl-10')}
      />
    </div>
  )

  // Spans the whole row (country included): long city names fit on a phone.
  const listeSuggestions = ouvert && (
    <ul
      id={`${idChamp}-liste`}
      role="listbox"
      className="absolute left-0 right-0 top-full z-20 mt-1.5 overflow-hidden rounded-xl border border-white/10 bg-[#12223D] py-1 shadow-xl"
    >
      {suggestions.length === 0 ? (
        <li className="px-3.5 py-2.5 text-[14px]" style={MUET}>
          {t('onboarding.step1.v3.aucuneSuggestion', { exemple: pays === 'CA' ? '514' : '212' })}
        </li>
      ) : (
        suggestions.map((s, i) => (
          <li
            key={`${s.type}-${s.lieu}`}
            id={`${idChamp}-${i}`}
            role="option"
            aria-selected={i === actif}
            // Pick on mouse down, before the field loses focus.
            onMouseDown={(e) => {
              e.preventDefault()
              choisirLieu(s)
            }}
            onMouseEnter={() => setActif(i)}
            className={cn(
              'flex min-h-11 cursor-pointer items-center justify-between gap-3 px-3.5 py-2 text-[14px]',
              i === actif && 'bg-white/[0.08]',
            )}
          >
            {s.type === 'indicatif' ? (
              <span className="min-w-0 truncate">
                <span className="font-semibold tabular-nums">{s.code}</span>
                {s.villes.length > 0 && <span style={MUET}> · {s.villes.join(', ')}</span>}
                {!s.villes.length && <span style={MUET}> · {t('onboarding.step1.v3.indicatif')}</span>}
              </span>
            ) : (
              <>
                <span className="min-w-0 truncate font-medium">{s.nom}</span>
                <span className="shrink-0 text-[13px] tabular-nums" style={MUET}>
                  {s.province} · {s.indicatifs.slice(0, 2).join(', ')}
                </span>
              </>
            )}
          </li>
        ))
      )}
    </ul>
  )

  const listeNumeros = (
    <div className="space-y-3">
      {avis && (
        <p className="text-[14px] leading-snug" style={MUET} role="status">
          {avis}
        </p>
      )}
      {(requete.isPending && requete.fetchStatus !== 'idle') || (!enSansFrais && attenteLieu) ? (
        <div className="grid grid-cols-2 gap-2" aria-label={t('onboarding.step1.searchingNumber')}>
          {Array.from({ length: PAGE }, (_, i) => (
            <span key={i} className="block h-[60px] rounded-xl bg-white/[0.06] animate-pulse" />
          ))}
        </div>
      ) : requete.isError ? (
        <div className="py-4 text-center text-[14px]" style={MUET}>
          <p>{tropDeRecherches ? t('onboarding.step1.v3.tropDeRecherches') : t('onboarding.step1.v3.erreur')}</p>
          {!tropDeRecherches && (
            <button
              type="button"
              onClick={() => requete.refetch()}
              className="mt-2 font-medium text-white/80 hover:text-white transition-colors"
            >
              {t('onboarding.step1.v3.reessayer')}
            </button>
          )}
        </div>
      ) : visibles.length > 0 ? (
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('onboarding.step1.title')}>
          {visibles.map((n) => {
            const choisi = n.phoneNumber === liste.choisi
            return (
              <button
                key={n.phoneNumber}
                type="button"
                role="radio"
                aria-checked={choisi}
                onClick={() => setListe((e) => ({ ...e, choisi: n.phoneNumber }))}
                className={cn(
                  'min-h-[60px] px-3 py-2 rounded-xl border text-left transition-colors',
                  choisi ? 'border-white bg-white/[0.08]' : 'border-white/[0.12] hover:border-white/30',
                )}
              >
                <span className="block text-[15px] font-semibold tabular-nums leading-tight">
                  {formatPhoneNumber(n.phoneNumber)}
                </span>
                <span className="mt-1 flex items-center justify-between gap-2 text-[12.5px] leading-tight" style={MUET}>
                  <span className="truncate">{n.locality || n.region || ' '}</span>
                  {choisi && <Check className="w-3.5 h-3.5 shrink-0 text-white" />}
                </span>
              </button>
            )
          })}
        </div>
      ) : (
        <p className="py-6 text-center text-[14px]" style={MUET}>
          {t('onboarding.step1.v3.aucun')}
        </p>
      )}
      {lot.length > 0 && (
        <div className="flex justify-end text-[14px] font-medium">
          <button
            type="button"
            onClick={autresNumeros}
            disabled={requete.isFetching}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors disabled:opacity-40"
          >
            <RefreshCw className={cn('w-4 h-4', requete.isFetching && 'animate-spin')} />
            {t('onboarding.step1.v2.autresNumeros')}
          </button>
        </div>
      )}
    </div>
  )

  return (
    <CadreAvant
      titre={t('onboarding.step1.title')}
      onContinuer={() => selectMutation.mutate(numeroEnvoye)}
      continuer={continuer}
      // En transfert, le bouton attend un numéro valide au lieu de répondre par une alerte « Numéro requis ».
      continuerDesactive={!numeroEnvoye || (mode === 'port' && !validatePhoneNumber(portNumber).isValid)}
      enCours={selectMutation.isPending}
    >
      <div className="space-y-5">
        {indisponible && (
          <p
            role="alert"
            className="rounded-xl px-4 py-3 text-[15px] leading-snug"
            style={{ backgroundColor: 'rgba(252,165,165,0.08)', border: '1px solid rgba(252,165,165,0.35)' }}
          >
            {t('onboarding.step1.v3.indisponible')}
          </p>
        )}
        {/* No transfer tab: people clicked it by mistake. Keeping a number is the link under the list. */}
        <div
          className="grid grid-cols-2 gap-1 p-1 rounded-xl"
          style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
          role="tablist"
        >
          {(['local', 'tollfree'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              className={cn(
                'h-10 rounded-lg text-[14px] font-medium transition-colors',
                mode === m ? 'bg-white text-[#0A192F]' : 'text-white/70 hover:text-white',
              )}
            >
              {t(`onboarding.step1.tabs.${m}`)}
            </button>
          ))}
        </div>

        {mode === 'port' && (
          <div>
            <Etiquette htmlFor="numero-a-transferer">{t('onboarding.step1.port.inputLabel')}</Etiquette>
            <input
              id="numero-a-transferer"
              type="tel"
              placeholder={t('onboarding.step1.port.inputPlaceholder')}
              value={portNumber}
              onChange={handlePortInput}
              aria-invalid={!!portError}
              className={cn(CHAMP_SOMBRE, 'tabular-nums', portError && 'border-[#FCA5A5]')}
            />
            {portError ? (
              <Erreur>{portError}</Erreur>
            ) : (
              <p className="mt-2 text-[13px]" style={MUET}>
                {t('onboarding.step1.port.teamContact')}
              </p>
            )}
          </div>
        )}

        <div className="space-y-3">
          {mode === 'port' && <Etiquette htmlFor={idChamp}>{t('onboarding.step1.v3.temporaire')}</Etiquette>}
          {/* Country and place on one line; toll-free numbers have no place to search. */}
          <div className={cn('relative grid gap-2', !enSansFrais && 'grid-cols-[auto_1fr]')}>
            {/* Without data-[size=default], the select's default height wins over h-12. */}
            <SelecteurPays
              sombre
              value={pays}
              onChange={changerPays}
              className="w-full h-12 data-[size=default]:h-12 rounded-xl text-[15px]"
            />
            {!enSansFrais && champLieu}
            {!enSansFrais && listeSuggestions}
          </div>
          {listeNumeros}
        </div>

        <p className="text-center text-[14px]" style={MUET}>
          {t(mode === 'port' ? 'onboarding.step1.garder.retourQuestion' : 'onboarding.step1.garder.question')}{' '}
          <button
            type="button"
            onClick={() => setMode(mode === 'port' ? 'local' : 'port')}
            className="font-medium text-white/85 underline-offset-2 hover:text-white hover:underline"
          >
            {t(mode === 'port' ? 'onboarding.step1.garder.retourLien' : 'onboarding.step1.garder.lien')}
          </button>
        </p>
      </div>
    </CadreAvant>
  )
}
